import { supabase } from './supabase'
import { Message, ConversationMetadata, User, MessageReaction, MessageRead, MessagePin } from '../types/database.types'
import { NotificationService } from './notificationService'

export class MessagingService {
  // Get all conversations for a user
  static async getConversations(userId: string, groupId?: string): Promise<ConversationMetadata[]> {
    try {
      let query = supabase
        .from('conversation_metadata')
        .select(`
          *,
          last_message:messages(
            id,
            content,
            created_at,
            sender_id,
            recipient_id
          )
        `)
        .or(`user1_id.eq.${userId},user2_id.eq.${userId}`)
        .order('last_message_at', { ascending: false })

      if (groupId) {
        query = query.eq('group_id', groupId)
      }

      const { data, error } = await query

      if (error) throw error

      // Add other user information to each conversation
      const conversationsWithUsers = await Promise.all(
        (data || []).map(async (conversation) => {
          const otherUserId = conversation.user1_id === userId ? conversation.user2_id : conversation.user1_id
          const { data: otherUser } = await supabase
            .from('users')
            .select('id, name, email, avatar_url')
            .eq('id', otherUserId)
            .single()

          return {
            ...conversation,
            other_user: otherUser
          }
        })
      )

      // Deduplicate conversations - keep only the most recent one for each user pair in each group
      const conversationMap = new Map()
      conversationsWithUsers.forEach(conversation => {
        const key = `${conversation.group_id}_${Math.min(conversation.user1_id, conversation.user2_id)}_${Math.max(conversation.user1_id, conversation.user2_id)}`
        
        if (!conversationMap.has(key) || 
            (conversation.last_message_at && 
             (!conversationMap.get(key).last_message_at || 
              new Date(conversation.last_message_at) > new Date(conversationMap.get(key).last_message_at)))) {
          conversationMap.set(key, conversation)
        }
      })

      return Array.from(conversationMap.values())
    } catch (error) {
      console.error('Error fetching conversations:', error)
      throw error
    }
  }

    // Get group conversations for a user (separate from direct message conversations)
  static async getGroupConversations(userId: string): Promise<any[]> {
    try {
      // First, get the group IDs where user is a member
      const { data: memberships, error: membershipsError } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', userId)

      if (membershipsError) {
        console.error('Error fetching group memberships:', membershipsError)
        throw membershipsError
      }

      // console.log('Raw memberships data:', memberships)

      if (!memberships || memberships.length === 0) return []

      // Get group details separately
      const groupIds = memberships.map(m => m.group_id)
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('id, name, description')
        .in('id', groupIds)

      if (groupsError) {
        console.error('Error fetching groups:', groupsError)
        throw groupsError
      }

      // console.log('Groups data:', groups)

      if (!groups || groups.length === 0) return []

      // For each group, get the latest group message
      const groupConversations = await Promise.all(
        groups.map(async (group) => {
          // console.log('Processing group:', group)

          // Get the latest group message for this group
          const { data: latestMessage, error: messageError } = await supabase
            .from('messages')
            .select('*')
            .eq('group_id', group.id)
            .eq('recipient_id', group.id) // Group messages have recipient_id = group_id
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle() // Use maybeSingle instead of single to avoid error when no messages

          if (messageError) {
            console.error('Error fetching latest message for group:', group.id, messageError)
          }

          // console.log('Latest message for group', group.name, ':', latestMessage)

          return {
            id: `group_${group.id}`,
            group_id: group.id,
            group_name: group.name,
            last_message: latestMessage || null,
            last_message_at: latestMessage?.created_at || null,
            type: 'group'
          }
        })
      )



      // Filter out null values and sort by latest message
      return groupConversations
        .filter((conv): conv is NonNullable<typeof conv> => conv !== null)
        .sort((a, b) => {
          if (!a.last_message_at && !b.last_message_at) return 0
          if (!a.last_message_at) return 1
          if (!b.last_message_at) return -1
          return new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
        })
    } catch (error) {
      console.error('Error fetching group conversations:', error)
      throw error
    }
  }

  // Get messages between two users in a group
  static async getMessages(
    userId1: string, 
    userId2: string, 
    groupId: string, 
    limit: number = 50
  ): Promise<Message[]> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('group_id', groupId)
        .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error

      // Batch fetch all unique user IDs
      const userIds = Array.from(new Set((data || []).flatMap(m => [m.sender_id, m.recipient_id])));
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, avatar_url')
        .in('id', userIds);
      if (usersError) throw usersError;
      const userMap = new Map(usersData.map(u => [u.id, u]));

      // Map user info to each message
      const messagesWithUsers = (data || []).map((message) => ({
        ...message,
        sender: userMap.get(message.sender_id),
        recipient: userMap.get(message.recipient_id)
      }));

      return messagesWithUsers.reverse(); // Reverse to show oldest first
    } catch (error) {
      console.error('Error fetching messages:', error)
      throw error
    }
  }

  // Get group messages for a specific group
  static async getGroupMessages(groupId: string, limit: number = 50): Promise<Message[]> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('group_id', groupId)
        .eq('recipient_id', groupId) // Group messages have recipient_id = group_id
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error

      // Batch fetch all unique sender IDs
      const userIds = Array.from(new Set((data || []).map(m => m.sender_id)));
      const { data: usersData, error: usersError } = await supabase
        .from('users')
        .select('id, name, email, avatar_url')
        .in('id', userIds);
      if (usersError) throw usersError;
      const userMap = new Map(usersData.map(u => [u.id, u]));

      // Map user info to each message
      const messagesWithUsers = (data || []).map((message) => ({
        ...message,
        sender: userMap.get(message.sender_id),
        recipient: null // Group messages don't have individual recipients
      }));

      return messagesWithUsers.reverse(); // Reverse to show oldest first
    } catch (error) {
      console.error('Error fetching group messages:', error)
      throw error
    }
  }

  // Send a message
  static async sendMessage(
    senderId: string,
    recipientId: string,
    groupId: string,
    content: string,
    messageType: 'text' | 'image' | 'file' | 'announcement' = 'text',
    fileUrl?: string,
    fileName?: string,
    fileSize?: number,
    parentId?: string | null
  ): Promise<Message> {
    try {
      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: senderId,
          recipient_id: recipientId,
          group_id: groupId,
          content,
          message_type: messageType,
          file_url: fileUrl,
          file_name: fileName,
          file_size: fileSize,
          parent_id: parentId || null
        })
        .select('*')
        .single()

      if (error) throw error

      // Get sender data (always needed)
      const { data: senderData } = await supabase
        .from('users')
        .select('id, name, email, avatar_url')
        .eq('id', senderId)
        .single()

      // For group messages, recipientId equals groupId, so don't fetch user data
      let recipientData = null
      if (recipientId !== groupId) {
        const { data: recData } = await supabase
          .from('users')
          .select('id, name, email, avatar_url')
          .eq('id', recipientId)
          .single()
        recipientData = recData
      }

      // Send notification for the message
      const isGroupConversation = recipientId === groupId
      let conversationId: string
      
      try {
        if (isGroupConversation) {
          // For group messages, use group ID as conversation ID
          conversationId = groupId
          
          // Ensure all group members are in conversation_participants
          const { data: groupMembers } = await supabase
            .from('group_members')
            .select('user_id')
            .eq('group_id', groupId)
          
          if (groupMembers && groupMembers.length > 0) {
            const participantInserts = groupMembers.map(member => ({
              conversation_id: conversationId,
              user_id: member.user_id
            }))
            
            // Insert participants (ignore conflicts if they already exist)
            await supabase
              .from('conversation_participants')
              .upsert(participantInserts, { onConflict: 'conversation_id,user_id' })
          }
        } else {
          // For direct messages, create a conversation between the two users
          conversationId = [senderId, recipientId].sort().join('_')
          
          // Ensure both users are in conversation_participants
          const participantInserts = [
            { conversation_id: conversationId, user_id: senderId },
            { conversation_id: conversationId, user_id: recipientId }
          ]
          
          // Insert participants (ignore conflicts if they already exist)
          await supabase
            .from('conversation_participants')
            .upsert(participantInserts, { onConflict: 'conversation_id,user_id' })
        }

        // Send notification for the message
        await NotificationService.createMessageNotification(
          data.id,
          senderId,
          conversationId,
          content,
          isGroupConversation
        )
      } catch (conversationError) {
        // If conversation participant setup fails, still send the message
        // but log the error for debugging
        console.error('Error setting up conversation participants:', conversationError)
        
        // Try to send notification without conversation participants
        try {
          const fallbackConversationId = isGroupConversation ? groupId : [senderId, recipientId].sort().join('_')
          await NotificationService.createMessageNotification(
            data.id,
            senderId,
            fallbackConversationId,
            content,
            isGroupConversation
          )
        } catch (notificationError) {
          console.error('Error sending notification:', notificationError)
        }
      }

      return {
        ...data,
        sender: senderData,
        recipient: recipientData
      }
    } catch (error) {
      console.error('Error sending message:', error)
      throw error
    }
  }

  // Mark messages as read
  static async markMessagesAsRead(
    recipientId: string,
    senderId: string,
    groupId: string
  ): Promise<void> {
    try {
      const { error } = await supabase.rpc('mark_messages_as_read', {
        p_recipient_id: recipientId,
        p_sender_id: senderId,
        p_group_id: groupId
      })

      if (error) throw error
    } catch (error) {
      console.error('Error marking messages as read:', error)
      throw error
    }
  }

  // Get unread message count for a user
  static async getUnreadCount(userId: string): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .eq('recipient_id', userId)
        .eq('is_read', false)

      if (error) throw error

      return count || 0
    } catch (error) {
      console.error('Error getting unread count:', error)
      throw error
    }
  }

  // Get group members for messaging
  static async getGroupMembers(groupId: string, currentUserId: string): Promise<User[]> {
    try {
      const { data, error } = await supabase
        .from('group_members')
        .select('user_id')
        .eq('group_id', groupId)
        .neq('user_id', currentUserId)

      if (error) throw error

      if (!data || data.length === 0) return []

      const userIds = data.map(item => item.user_id)
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select('*')
        .in('id', userIds)

      if (usersError) throw usersError

      return users || []
    } catch (error) {
      console.error('Error fetching group members:', error)
      throw error
    }
  }

  // Delete a message (soft delete)
  static async deleteMessage(messageId: string, userId: string): Promise<void> {
    try {
      const { data: message } = await supabase
        .from('messages')
        .select('sender_id, recipient_id')
        .eq('id', messageId)
        .single()

      if (!message) throw new Error('Message not found')

      const updateField = message.sender_id === userId ? 'is_deleted_by_sender' : 'is_deleted_by_recipient'

      const { error } = await supabase
        .from('messages')
        .update({ [updateField]: true })
        .eq('id', messageId)

      if (error) throw error
    } catch (error) {
      console.error('Error deleting message:', error)
      throw error
    }
  }

  // Subscribe to new messages
  static subscribeToMessages(userId: string, callback: (message: Message) => void) {
    const channel = supabase
      .channel('messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `recipient_id=eq.${userId}`
        },
        (payload) => {
          callback(payload.new as Message)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Subscribe to conversation updates
  static subscribeToConversations(userId: string, callback: (conversation: ConversationMetadata) => void) {
    const channel = supabase
      .channel('conversations')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'conversation_metadata',
          filter: `or(user1_id.eq.${userId},user2_id.eq.${userId})`
        },
        (payload) => {
          callback(payload.new as ConversationMetadata)
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Fetch reactions for a list of message IDs
  static async getReactionsForMessages(messageIds: string[]): Promise<{ [messageId: string]: MessageReaction[] }> {
    if (!messageIds.length) return {}
    const { data, error } = await supabase
      .from('message_reactions')
      .select('*')
      .in('message_id', messageIds)
    if (error) throw error
    const reactionsByMessage: { [messageId: string]: MessageReaction[] } = {}
    for (const reaction of data || []) {
      if (!reactionsByMessage[reaction.message_id]) reactionsByMessage[reaction.message_id] = []
      reactionsByMessage[reaction.message_id].push(reaction)
    }
    return reactionsByMessage
  }

  // Add a reaction to a message
  static async addReaction(messageId: string, userId: string, emoji: string): Promise<void> {
    const { error } = await supabase
      .from('message_reactions')
      .insert({ message_id: messageId, user_id: userId, emoji })
    if (error) throw error
  }

  // Update message content (edit message)
  static async updateMessageContent(messageId: string, newContent: string): Promise<void> {
    const { error } = await supabase
      .from('messages')
      .update({ content: newContent, edited_at: new Date().toISOString() })
      .eq('id', messageId)
    if (error) throw error
  }

  // Subscribe to new reactions for messages in a group
  static subscribeToMessageReactions(groupId: string, callback: (reaction: MessageReaction) => void) {
    const channel = supabase
      .channel('message_reactions')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_reactions',
          filter: `group_id=eq.${groupId}` // Note: group_id is not in message_reactions, so we need to filter by message_id in frontend
        },
        (payload) => {
          callback(payload.new as MessageReaction)
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Subscribe to message edits for messages in a group
  static subscribeToMessageEdits(groupId: string, callback: (message: Message) => void) {
    const channel = supabase
      .channel('message_edits')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          callback(payload.new as Message)
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Subscribe to message deletions for messages in a group
  static subscribeToMessageDeletions(groupId: string, callback: (messageId: string) => void) {
    const channel = supabase
      .channel('message_deletions')
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'messages',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          callback(payload.old.id)
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Delete a reaction for a message by user and emoji
  static async deleteReaction(messageId: string, userId: string, emoji: string): Promise<void> {
    const { error } = await supabase
      .from('message_reactions')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
      .eq('emoji', emoji)
    if (error) throw error
  }

  // Subscribe to new messages for a group
  static subscribeToNewMessages(groupId: string, callback: (message: Message) => void) {
    const channel = supabase
      .channel('new_messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          callback(payload.new as Message)
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Broadcast typing status (start/stop) for a group
  static broadcastTypingStatus(groupId: string, userId: string, status: 'start' | 'stop') {
    supabase.channel(`typing_status_${groupId}`)
      .send({
        type: 'broadcast',
        event: 'typing',
        payload: { userId, status }
      })
  }

  // Subscribe to typing status events for a group
  static subscribeToTypingStatus(groupId: string, callback: (userId: string, status: 'start' | 'stop') => void) {
    const channel = supabase.channel(`typing_status_${groupId}`)
      .on('broadcast', { event: 'typing' }, (payload) => {
        const { userId, status } = payload.payload
        callback(userId, status)
      })
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Fetch reads for a list of message IDs
  static async getMessageReads(messageIds: string[]): Promise<{ [messageId: string]: MessageRead[] }> {
    if (!messageIds.length) return {}
    const { data, error } = await supabase
      .from('message_reads')
      .select('*')
      .in('message_id', messageIds)
    if (error) throw error
    const readsByMessage: { [messageId: string]: MessageRead[] } = {}
    for (const read of data || []) {
      if (!readsByMessage[read.message_id]) readsByMessage[read.message_id] = []
      readsByMessage[read.message_id].push(read)
    }
    return readsByMessage
  }

  // Add a read receipt for a message
  static async addMessageRead(messageId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('message_reads')
      .insert({ message_id: messageId, user_id: userId })
    if (error) throw error
  }

  // Subscribe to new message reads for a group
  static subscribeToMessageReads(groupId: string, callback: (read: MessageRead) => void) {
    const channel = supabase
      .channel('message_reads')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_reads',
          filter: `group_id=eq.${groupId}` // Note: group_id is not in message_reads, so filter in frontend
        },
        (payload) => {
          callback(payload.new as MessageRead)
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }

  // Fetch pins for a list of message IDs
  static async getMessagePins(messageIds: string[]): Promise<{ [messageId: string]: MessagePin[] }> {
    if (!messageIds.length) return {}
    const { data, error } = await supabase
      .from('message_pins')
      .select('*')
      .in('message_id', messageIds)
    if (error) throw error
    const pinsByMessage: { [messageId: string]: MessagePin[] } = {}
    for (const pin of data || []) {
      if (!pinsByMessage[pin.message_id]) pinsByMessage[pin.message_id] = []
      pinsByMessage[pin.message_id].push(pin)
    }
    return pinsByMessage
  }

  // Add a pin for a message
  static async addMessagePin(messageId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('message_pins')
      .insert({ message_id: messageId, user_id: userId })
    if (error) throw error
  }

  // Remove a pin for a message
  static async removeMessagePin(messageId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('message_pins')
      .delete()
      .eq('message_id', messageId)
      .eq('user_id', userId)
    if (error) throw error
  }

  // Subscribe to pin/unpin events for a group
  static subscribeToMessagePins(groupId: string, callback: (pin: MessagePin, event: 'INSERT' | 'DELETE') => void) {
    const channel = supabase
      .channel('message_pins')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'message_pins',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          callback(payload.new as MessagePin, 'INSERT')
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'message_pins',
          filter: `group_id=eq.${groupId}`
        },
        (payload) => {
          callback(payload.old as MessagePin, 'DELETE')
        }
      )
      .subscribe()
    return () => {
      supabase.removeChannel(channel)
    }
  }
} 